import { parseIsoToDate } from './iso';
import { ISO8601 } from './type/iso8601';

/**
 * Checks if the given date `isDateEqual` to the reference `anchorPoint`.
 * @param theDateInQuestion - The date. It can be undefined which will always result in false.
 * @param anchorPoint - The point of reference for determining what is "equal" to.
 * @returns true if theDateInQuestion is equal to the anchorPoint; otherwise, false.
 */
export const isDateEqual = (theDateInQuestion: ISO8601 | Date | undefined, anchorPoint: ISO8601 | Date): boolean => {
  if (theDateInQuestion === undefined) {
    return false;
  }

  return parseIsoToDate(theDateInQuestion).getTime() === parseIsoToDate(anchorPoint).getTime();
};

/**
 * isBefore will tell you if theDateInQuestion is before the anchorPoint or not
 * @param theDateInQuestion - The date. It can be undefined which will always result in false.
 * @param anchorPoint - The point of reference for determining what is "before" and what is "after".
 * @returns true if theDateInQuestion is less than or equal to the anchorPoint otherwise false.
 */
export const isBefore = (theDateInQuestion: ISO8601 | Date | undefined, anchorPoint: ISO8601 | Date): boolean => {
  if (theDateInQuestion === undefined) {
    return false;
  }

  return parseIsoToDate(theDateInQuestion).getTime() <= parseIsoToDate(anchorPoint).getTime();
};

/**
 * isAfter will tell you if theDateInQuestion is after the anchorPoint or not
 * @param theDateInQuestion - The date. It can be undefined which will always result in false.
 * @param anchorPoint - The point of reference for determining what is "before" and what is "after".
 * @returns true if theDateInQuestion is greater than the anchorPoint otherwise false.
 */
export const isAfter = (theDateInQuestion: ISO8601 | Date | undefined, anchorPoint: ISO8601 | Date): boolean => {
  if (theDateInQuestion === undefined) {
    return false;
  }

  return parseIsoToDate(theDateInQuestion).getTime() > parseIsoToDate(anchorPoint).getTime();
};

/**
 * isPast will tell you if theDateInQuestion is in the past or not
 * @param theDateInQuestion - The date that might be in the past. It can be undefined which will always result in false.
 * @param deprecated_use_isBefore - **deprecated**; use `isBefore(theDateInQuestion, the anchorPoint)` instead.
 * @returns true if theDateInQuestion is less than or equal to "now" otherwise false.
 */
export const isPast = (
  theDateInQuestion: ISO8601 | Date | undefined,
  deprecated_use_isBefore: ISO8601 | Date = new Date()
): boolean => {
  return isBefore(theDateInQuestion, deprecated_use_isBefore);
};

/**
 * isFuture will tell you if theDateInQuestion is in the future or not
 * @param theDateInQuestion - The date that might be in the future. It can be undefined which will always result in false.
 * @param deprecated_use_isAfter - **deprecated**; use `isAfter(theDateInQuestion, the anchorPoint)` instead.
 * @returns true if theDateInQuestion is greater than to "now" otherwise false.
 */
export const isFuture = (
  theDateInQuestion: ISO8601 | Date | undefined,
  deprecated_use_isAfter: ISO8601 | Date = new Date()
): boolean => {
  return isAfter(theDateInQuestion, deprecated_use_isAfter);
};

/**
 * minDate will return the minimum date from an array of dates
 * @param dates - The array of dates to compare. It can contain ISO8601 strings or Date objects.
 * @returns the earliest date from the array.
 */
export const minDate = (dates: Array<ISO8601 | Date>): Date => {
  if (dates.length === 0) {
    throw new Error('Cannot get min date of empty array');
  }

  return dates.reduce((min: Date, date: ISO8601 | Date) => {
    if (isBefore(date, min)) {
      return parseIsoToDate(date);
    }
    return min;
  }, parseIsoToDate(dates[0]!));
};

/**
 * maxDate will return the maximum date from an array of dates
 * @param dates - The array of dates to compare. It can contain ISO8601 strings or Date objects.
 * @returns the latest date from the array.
 */
export const maxDate = (dates: Array<ISO8601 | Date>): Date => {
  if (dates.length === 0) {
    throw new Error('Cannot get max date of empty array');
  }

  return dates.reduce((max: Date, date: ISO8601 | Date) => {
    if (isAfter(date, max)) {
      return parseIsoToDate(date);
    }
    return max;
  }, parseIsoToDate(dates[0]!));
};
